import { useMutation } from '@apollo/client/react';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { BTC_GAME_FRAGMENT } from '@optools/services/operator-gateway/btc/fragments';
import {
  ARCHIVE_MEDIA,
  START_MEDIA,
  STOP_MEDIA,
  UNSCHEDULE_MEDIA,
} from '@optools/services/operator-gateway/btc/mutations';
import { Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { useNavigate } from '@tanstack/react-router';
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { toast } from 'sonner';

export type PipelineControlParams = {
  gamePk: number;
  mediaId: string;
  pipelineId: number;
  region?: Region;
  navigateOnSuccess?: boolean;
};

type MutationResult = { mediaId: string; reason?: string | null; success: boolean };

type PipelineControlsContextValue = {
  archiveMedia: (params: PipelineControlParams) => void;
  archiveMediaLoading: boolean;
  startMedia: (params: PipelineControlParams) => void;
  startMediaLoading: boolean;
  stopMedia: (params: PipelineControlParams) => void;
  stopMediaLoading: boolean;
  unscheduleMedia: (params: PipelineControlParams) => void;
  unscheduleMediaLoading: boolean;
};

const PipelineControlsContext = createContext<PipelineControlsContextValue | null>(null);

// Module scope so it's a stable reference and safe to omit from hook deps.
function notifyResult(errorTitle: string, successTitle: string, result: MutationResult) {
  if (result.success) {
    toast.success(successTitle, { description: `Media ID: ${result.mediaId}` });
  } else {
    toast.error(errorTitle, {
      description: (
        <>
          <h2>Media ID: {result.mediaId}</h2>
          <p>{result.reason}</p>
        </>
      ),
      duration: Infinity,
    });
  }
}

export function PipelineControlsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const [unscheduleMediaMutation, unscheduleStatus] = useMutation(UNSCHEDULE_MEDIA);
  const [startMediaMutation, startStatus] = useMutation(START_MEDIA);
  const [stopMediaMutation, stopStatus] = useMutation(STOP_MEDIA);
  const [archiveMediaMutation, archiveStatus] = useMutation(ARCHIVE_MEDIA);

  const unscheduleMedia = useCallback(
    ({ gamePk, mediaId, navigateOnSuccess, pipelineId, region }: PipelineControlParams) =>
      void unscheduleMediaMutation({
        onCompleted: ({ unscheduleMedia }) => {
          const [result] = unscheduleMedia;
          if (result.success) {
            toast.success('Unschedule Media Success', {
              description: `Media ID: ${result.mediaId}`,
            });
            if (navigateOnSuccess) {
              navigate({
                params: { gamePk },
                search: (params) => ({ date: params.date }),
                to: '/btc-tools/schedule/$gamePk/pipelines',
              });
            }
          } else {
            notifyResult('Unschedule Media Error', 'Unschedule Media Success', result);
          }
        },
        onError: (error) =>
          displayApolloError({
            error,
            options: { description: `Media ID: ${mediaId}` },
            title: 'Unschedule Media Error',
          }),
        update: (cache, { data }) => {
          if (data?.unscheduleMedia.length) {
            const [{ success }] = data.unscheduleMedia;
            if (success) {
              const game = cache.readFragment({
                fragment: BTC_GAME_FRAGMENT,
                fragmentName: 'BTCGameFields',
                id: cache.identify({ __typename: 'Game', gamePk }),
              });

              if (game) {
                if (region) {
                  cache.writeFragment({
                    data: {
                      ...game,
                      pipelines: game.pipelines.map((pipeline) =>
                        pipeline.id === pipelineId
                          ? {
                              ...pipeline,
                              regionalPipelines: pipeline.regionalPipelines.filter(
                                (regionalPipeline) => regionalPipeline.region !== region,
                              ),
                            }
                          : pipeline,
                      ),
                    },
                    fragment: BTC_GAME_FRAGMENT,
                    fragmentName: 'BTCGameFields',
                    id: cache.identify({ __typename: 'Game', gamePk }),
                  });
                } else {
                  cache.writeFragment({
                    data: {
                      ...game,
                      pipelines: game.pipelines.filter((pipeline) => pipeline.id !== pipelineId),
                    },
                    fragment: BTC_GAME_FRAGMENT,
                    fragmentName: 'BTCGameFields',
                    id: cache.identify({ __typename: 'Game', gamePk }),
                  });

                  cache.evict({
                    id: cache.identify({ __typename: 'MediaPipeline', id: pipelineId }),
                  });
                  cache.gc();
                }
              }
            }
          }
        },
        variables: { mediaIds: [mediaId], region },
      }),
    [navigate, unscheduleMediaMutation],
  );

  const startMedia = useCallback(
    ({ mediaId, region }: PipelineControlParams) =>
      void startMediaMutation({
        onCompleted: ({ startMedia }) =>
          notifyResult('Start Media Error', 'Start Media Success', startMedia[0]),
        onError: (error) =>
          displayApolloError({
            error,
            options: { description: `Media ID: ${mediaId}` },
            title: 'Start Media Error',
          }),
        variables: { mediaIds: [mediaId], region },
      }),
    [startMediaMutation],
  );

  const stopMedia = useCallback(
    ({ mediaId, region }: PipelineControlParams) =>
      void stopMediaMutation({
        onCompleted: ({ stopMedia }) =>
          notifyResult('Stop Media Error', 'Stop Media Success', stopMedia[0]),
        onError: (error) =>
          displayApolloError({
            error,
            options: { description: `Media ID: ${mediaId}` },
            title: 'Stop Media Error',
          }),
        variables: { mediaIds: [mediaId], region },
      }),
    [stopMediaMutation],
  );

  const archiveMedia = useCallback(
    ({ mediaId, region }: PipelineControlParams) =>
      void archiveMediaMutation({
        onCompleted: ({ archiveMedia }) =>
          notifyResult('Archive Media Error', 'Archive Media Success', archiveMedia[0]),
        onError: (error) =>
          displayApolloError({
            error,
            options: { description: `Media ID: ${mediaId}` },
            title: 'Archive Media Error',
          }),
        variables: { mediaIds: [mediaId], region },
      }),
    [archiveMediaMutation],
  );

  const value = useMemo<PipelineControlsContextValue>(
    () => ({
      archiveMedia,
      archiveMediaLoading: archiveStatus.loading,
      startMedia,
      startMediaLoading: startStatus.loading,
      stopMedia,
      stopMediaLoading: stopStatus.loading,
      unscheduleMedia,
      unscheduleMediaLoading: unscheduleStatus.loading,
    }),
    [
      archiveMedia,
      archiveStatus.loading,
      startMedia,
      startStatus.loading,
      stopMedia,
      stopStatus.loading,
      unscheduleMedia,
      unscheduleStatus.loading,
    ],
  );

  return (
    <PipelineControlsContext.Provider value={value}>{children}</PipelineControlsContext.Provider>
  );
}

export function usePipelineControls() {
  const context = useContext(PipelineControlsContext);
  if (!context) {
    throw new Error('usePipelineControls must be used within a PipelineControlsProvider');
  }
  return context;
}
